import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useEmpresa } from '../../contexts/EmpresaContext';
import { episService } from '../../services/epis.service';
import { useToast, ToastContainer } from '../../components/Toast';
import { EntregaEpiModal } from './components/EntregaEpiModal';
import { EntradaEstoqueModal } from './components/EntradaEstoqueModal';
import { BaixaEstoqueModal } from './components/BaixaEstoqueModal';
import { DevolucaoEpiModal } from './components/DevolucaoEpiModal';
import { DetalhesEntregaModal } from './components/DetalhesEntregaModal';
import { AssinarEntregaModal } from './components/AssinarEntregaModal';
import {
  ShieldCheck,
  Package,
  Calendar,
  Undo2,
  Users,
  AlertTriangle,
  CheckCircle2,
  Search,
  RefreshCw,
  Plus,
  PackagePlus,
  PackageMinus,
  ChevronDown,
  ChevronRight,
  AlertCircle,
  Clock,
  Layers,
  FileText,
  FileDown,
  MessageCircle,
  Copy,
  PenTool,
  Camera,
  Check,
  Table as TableIcon,
} from 'lucide-react';
import type {
  EpiKpisContract,
  EpiMapaCargoItemContract,
  EpiEstoqueSaldoContract,
  EpiMovimentacaoEstoqueContract,
  EpiCronogramaVigenciaItemContract,
  EpiRadarDemissionalItemContract,
  EpiDevolucaoItemContract,
  EpiEntregaContract,
  EpiMatrizContract,
} from '@sstexames/shared-types';

type EpiTabType = 'mapa' | 'entregas' | 'estoque' | 'vigencias' | 'devolucoes' | 'matriz';

export default function GestaoEpisPage() {
  const { empresa } = useEmpresa();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab State with URL query sync
  const tabParam = searchParams.get('tab') as EpiTabType | null;
  const [activeTab, setActiveTab] = useState<EpiTabType>(
    tabParam && ['mapa', 'entregas', 'estoque', 'vigencias', 'devolucoes', 'matriz'].includes(tabParam)
      ? tabParam
      : 'mapa',
  );

  useEffect(() => {
    if (tabParam && tabParam !== activeTab) {
      if (['mapa', 'entregas', 'estoque', 'vigencias', 'devolucoes', 'matriz'].includes(tabParam)) {
        setActiveTab(tabParam);
      }
    }
  }, [tabParam, activeTab]);

  const handleTabChange = (newTab: EpiTabType) => {
    setActiveTab(newTab);
    setSearchParams({ tab: newTab });
  };

  // Loading states
  const [loadingKpis, setLoadingKpis] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Data states
  const [kpis, setKpis] = useState<EpiKpisContract | null>(null);
  const [mapaCargos, setMapaCargos] = useState<EpiMapaCargoItemContract[]>([]);
  const [entregas, setEntregas] = useState<EpiEntregaContract[]>([]);
  const [estoqueSaldos, setEstoqueSaldos] = useState<EpiEstoqueSaldoContract[]>([]);
  const [movimentacoes, setMovimentacoes] = useState<EpiMovimentacaoEstoqueContract[]>([]);
  const [vigencias, setVigencias] = useState<EpiCronogramaVigenciaItemContract[]>([]);
  const [radarDemissional, setRadarDemissional] = useState<EpiRadarDemissionalItemContract[]>([]);
  const [devolucoes, setDevolucoes] = useState<EpiDevolucaoItemContract[]>([]);
  const [matriz, setMatriz] = useState<EpiMatrizContract | null>(null);

  // Filtering states
  const [searchQuery, setSearchQuery] = useState('');
  const [vigenciaFilter, setVigenciaFilter] = useState<'todos' | 'criticos' | 'vencidos'>('todos');
  const [entregaStatusFilter, setEntregaStatusFilter] = useState<'todos' | 'entregue' | 'pendente_assinatura'>('todos');
  const [expandedCargos, setExpandedCargos] = useState<Record<number, boolean>>({});

  // Modals state
  const [isEntregaModalOpen, setIsEntregaModalOpen] = useState(false);
  const [isEntradaModalOpen, setIsEntradaModalOpen] = useState(false);
  const [isBaixaModalOpen, setIsBaixaModalOpen] = useState(false);
  const [isDevolucaoModalOpen, setIsDevolucaoModalOpen] = useState(false);
  const [isDetalhesModalOpen, setIsDetalhesModalOpen] = useState(false);
  const [isAssinarModalOpen, setIsAssinarModalOpen] = useState(false);

  // Modal Selections
  const [modalColaboradorId, setModalColaboradorId] = useState<number | undefined>(undefined);
  const [modalAgendamentoId, setModalAgendamentoId] = useState<number | undefined>(undefined);
  const [selectedEntrega, setSelectedEntrega] = useState<EpiEntregaContract | null>(null);
  const [copiedTokenId, setCopiedTokenId] = useState<number | null>(null);

  // 1. Carregar KPIs
  const loadKpis = useCallback(async () => {
    if (!empresa?.id) return;
    try {
      setLoadingKpis(true);
      const data = await episService.getKpis(empresa.id);
      setKpis(data);
    } catch (err) {
      console.error('Erro ao carregar KPIs de EPIs:', err);
    } finally {
      setLoadingKpis(false);
    }
  }, [empresa?.id]);

  // 2. Carregar dados da aba ativa
  const loadTabData = useCallback(async () => {
    if (!empresa?.id) return;
    try {
      setLoadingData(true);
      setErrorMessage(null);

      if (activeTab === 'mapa') {
        const data = await episService.getMapaCargos(empresa.id);
        setMapaCargos(data);
      } else if (activeTab === 'entregas') {
        const data = await episService.getEntregas(empresa.id);
        setEntregas(data);
      } else if (activeTab === 'estoque') {
        const [saldosData, movsData] = await Promise.all([
          episService.getEstoqueSaldos(empresa.id),
          episService.getMovimentacoesEstoque(empresa.id),
        ]);
        setEstoqueSaldos(saldosData);
        setMovimentacoes(movsData);
      } else if (activeTab === 'vigencias') {
        const data = await episService.getCronogramaVigencias(empresa.id);
        setVigencias(data);
      } else if (activeTab === 'devolucoes') {
        const [radarData, devData] = await Promise.all([
          episService.getRadarDemissional(empresa.id),
          episService.getDevolucoes(empresa.id),
        ]);
        setRadarDemissional(radarData);
        setDevolucoes(devData);
      } else if (activeTab === 'matriz') {
        const data = await episService.getMatriz(empresa.id);
        setMatriz(data);
      }
    } catch (err: unknown) {
      console.error('Erro ao carregar dados da aba:', err);
      const errorMsg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao carregar informações de EPIs.';
      setErrorMessage(errorMsg);
    } finally {
      setLoadingData(false);
    }
  }, [empresa?.id, activeTab]);

  useEffect(() => {
    loadKpis();
    loadTabData();
  }, [loadKpis, loadTabData]);

  // Toggle Cargo accordion
  const toggleCargo = (cargoId: number) => {
    setExpandedCargos((prev) => ({ ...prev, [cargoId]: !prev[cargoId] }));
  };

  // Filtered Cargo Map
  const filteredCargos = useMemo(() => {
    if (!searchQuery.trim()) return mapaCargos;
    const query = searchQuery.toLowerCase();
    return mapaCargos.filter(
      (c) =>
        c.cargo_nome.toLowerCase().includes(query) ||
        c.setor_nome?.toLowerCase().includes(query) ||
        c.colaboradores.some((colab) => colab.colaborador_nome.toLowerCase().includes(query)),
    );
  }, [mapaCargos, searchQuery]);

  // Filtered Entregas
  const filteredEntregas = useMemo(() => {
    return entregas.filter((e) => {
      if (entregaStatusFilter !== 'todos' && e.status !== entregaStatusFilter) {
        return false;
      }
      if (!searchQuery.trim()) return true;
      const query = searchQuery.toLowerCase();
      return (
        e.id.toString().includes(query) ||
        e.colaborador_nome.toLowerCase().includes(query) ||
        e.colaborador_cpf.includes(query) ||
        e.cargo_nome.toLowerCase().includes(query) ||
        e.itens.some((it) => it.descricao_epi.toLowerCase().includes(query))
      );
    });
  }, [entregas, entregaStatusFilter, searchQuery]);

  // Filtered Vigências
  const filteredVigencias = useMemo(() => {
    return vigencias.filter((v) => {
      const matchSearch =
        !searchQuery ||
        v.colaborador_nome.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (v.descricao_epi || v.epi_descricao || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
        v.cargo_nome.toLowerCase().includes(searchQuery.toLowerCase());

      if (!matchSearch) return false;

      if (vigenciaFilter === 'criticos') {
        return (
          (v.status_vigencia as string) === 'a_vencer' ||
          v.status_vigencia === 'alerta_30_dias' ||
          v.status_vigencia === 'alerta_15_dias'
        );
      }
      if (vigenciaFilter === 'vencidos') return v.status_vigencia === 'vencido';
      return true;
    });
  }, [vigencias, searchQuery, vigenciaFilter]);

  // Quick Action Handlers
  const handleOpenEntregaForColaborador = (colaboradorId: number) => {
    setModalColaboradorId(colaboradorId);
    setIsEntregaModalOpen(true);
  };

  const handleOpenDevolucaoForColaborador = (colaboradorId: number, agendamentoId?: number) => {
    setModalColaboradorId(colaboradorId);
    setModalAgendamentoId(agendamentoId);
    setIsDevolucaoModalOpen(true);
  };

  const handleViewDetalhes = (entrega: EpiEntregaContract) => {
    setSelectedEntrega(entrega);
    setIsDetalhesModalOpen(true);
  };

  const handleOpenAssinatura = (entrega: EpiEntregaContract) => {
    setSelectedEntrega(entrega);
    setIsAssinarModalOpen(true);
  };

  const handleDownloadPdf = async (entrega: EpiEntregaContract) => {
    try {
      await episService.downloadPdfFile(
        entrega.id,
        `ficha_epi_${entrega.id}_${entrega.colaborador_nome.replace(/\s+/g, '_')}.pdf`,
        entrega.empresa_id,
      );
      toast.success('Download do PDF NR-06 iniciado.');
    } catch (err) {
      console.error('Erro ao baixar PDF:', err);
      toast.error('Erro ao gerar PDF da ficha de EPI.');
    }
  };

  const handleCopyLink = (entrega: EpiEntregaContract) => {
    if (!entrega.link_assinatura_publico) return;
    navigator.clipboard.writeText(entrega.link_assinatura_publico);
    setCopiedTokenId(entrega.id);
    toast.success('Link de autoatendimento copiado!');
    setTimeout(() => setCopiedTokenId(null), 2500);
  };

  const handleShareWhatsApp = (entrega: EpiEntregaContract) => {
    if (!entrega.link_assinatura_publico) return;
    const msg = encodeURIComponent(
      `Olá ${entrega.colaborador_nome}, acesse o link seguro para assinar digitalmente o recebimento dos seus EPIs (NR-06):\n${entrega.link_assinatura_publico}`,
    );
    window.open(`https://api.whatsapp.com/send?text=${msg}`, '_blank');
  };

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-[1600px] mx-auto min-h-screen">
      <ToastContainer toasts={toast.toasts} onRemove={toast.removeToast} />

      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5 mb-1">
            <div className="w-9 h-9 rounded-xl bg-primary-600 text-white flex items-center justify-center shadow-xs">
              <ShieldCheck className="w-5 h-5" />
            </div>
            <h1 className="text-xl font-bold text-gray-900">Gestão de EPIs e EPCs</h1>
            <span className="text-xs bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2.5 py-0.5 font-medium">
              NR-06 Digital & Auditoria
            </span>
          </div>
          <p className="text-xs text-gray-500">
            Controle de entregas com biometria facial, vigência de equipamentos, estoque Kardex, devoluções e matriz PGR.
          </p>
        </div>

        {/* Global Action Buttons */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => {
              setModalColaboradorId(undefined);
              setIsEntregaModalOpen(true);
            }}
            className="px-3.5 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-xl text-xs font-semibold shadow-xs flex items-center gap-1.5 transition-colors"
          >
            <Plus className="w-4 h-4" /> Nova Entrega de EPI
          </button>

          <button
            onClick={() => setIsEntradaModalOpen(true)}
            className="px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-medium shadow-2xs flex items-center gap-1.5 transition-colors"
          >
            <PackagePlus className="w-4 h-4 text-emerald-600" /> Entrada / Compra
          </button>

          <button
            onClick={() => setIsBaixaModalOpen(true)}
            className="px-3 py-2 bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-medium shadow-2xs flex items-center gap-1.5 transition-colors"
          >
            <PackageMinus className="w-4 h-4 text-rose-600" /> Baixa / Avaria
          </button>

          <button
            onClick={() => {
              loadKpis();
              loadTabData();
            }}
            disabled={loadingData || loadingKpis}
            title="Atualizar dados"
            className="p-2 border border-gray-300 bg-white rounded-xl hover:bg-gray-50 text-gray-600 hover:text-gray-900 transition-colors shadow-2xs disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loadingData || loadingKpis ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-3 rounded-xl text-xs flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 flex-shrink-0" />
            <span>{errorMessage}</span>
          </div>
          <button onClick={() => setErrorMessage(null)} className="text-rose-500 hover:text-rose-700 text-xs">
            Fechar
          </button>
        </div>
      )}

      {/* 5 KPIs Estratégicos */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3.5">
        {/* KPI 1: Cobertura Colaboradores */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-2xs hover:border-gray-300 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              Cobertura Colaboradores
            </span>
            <div className="w-8 h-8 rounded-lg bg-emerald-50 text-emerald-600 border border-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2.5 flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-gray-900">
              {loadingKpis ? '...' : `${kpis?.cobertura_colaboradores_pct ?? kpis?.taxa_cobertura_colaboradores_pct ?? 0}%`}
            </span>
            <span className="text-[11px] text-gray-500">100% equipados</span>
          </div>
          <div className="mt-2.5">
            <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded px-2 py-0.5 inline-block font-medium">
              {kpis?.colaboradores_100_equipados ?? 0} de {kpis?.total_colaboradores_demandantes ?? kpis?.total_colaboradores ?? 0} colaboradores
            </span>
          </div>
        </div>

        {/* KPI 2: Cobertura Requisitos */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-2xs hover:border-gray-300 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              Requisitos de EPI
            </span>
            <div className="w-8 h-8 rounded-lg bg-primary-50 text-primary-600 border border-primary-100 flex items-center justify-center">
              <ShieldCheck className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2.5 flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-gray-900">
              {loadingKpis ? '...' : `${kpis?.cobertura_requisitos_pct ?? kpis?.taxa_cobertura_requisitos_pct ?? 0}%`}
            </span>
            <span className="text-[11px] text-gray-500">atendimento PGR</span>
          </div>
          <div className="mt-2.5">
            <span className="text-[10px] text-primary-700 bg-primary-50 border border-primary-100 rounded px-2 py-0.5 inline-block font-medium">
              {kpis?.total_epis_vigentes_em_uso ?? kpis?.total_epis_entregues_vigentes ?? 0} de {kpis?.total_epis_demandados ?? ((kpis?.total_epis_vigentes_em_uso ?? 0) + (kpis?.total_epis_faltantes ?? 0))} itens devidos
            </span>
          </div>
        </div>

        {/* KPI 3: Vencimentos Próximos */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-2xs hover:border-gray-300 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              A Vencer / Vencidos
            </span>
            <div className="w-8 h-8 rounded-lg bg-amber-50 text-amber-600 border border-amber-100 flex items-center justify-center">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2.5 flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-amber-600">
              {loadingKpis
                ? '...'
                : (kpis?.total_epis_a_vencer_30d ?? kpis?.epis_a_vencer_30d ?? 0) +
                  (kpis?.total_epis_vencidos ?? kpis?.epis_vencidos ?? 0)}
            </span>
            <span className="text-[11px] text-gray-500">troca / inspeção</span>
          </div>
          <div className="mt-2.5">
            <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-100 rounded px-2 py-0.5 inline-block font-medium">
              {kpis?.total_epis_a_vencer_30d ?? kpis?.epis_a_vencer_30d ?? 0} próximos (30d) | {kpis?.total_epis_vencidos ?? kpis?.epis_vencidos ?? 0} vencidos
            </span>
          </div>
        </div>

        {/* KPI 4: Ruptura de Estoque */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-2xs hover:border-gray-300 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              Ruptura de Estoque
            </span>
            <div className="w-8 h-8 rounded-lg bg-rose-50 text-rose-600 border border-rose-100 flex items-center justify-center">
              <AlertTriangle className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2.5 flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-rose-600">
              {loadingKpis ? '...' : (kpis?.itens_abaixo_estoque_minimo ?? kpis?.alertas_ruptura_estoque ?? 0)}
            </span>
            <span className="text-[11px] text-gray-500">EPIs críticos</span>
          </div>
          <div className="mt-2.5">
            <span className="text-[10px] text-rose-700 bg-rose-50 border border-rose-100 rounded px-2 py-0.5 inline-block font-medium">
              Saldo &le; estoque mínimo
            </span>
          </div>
        </div>

        {/* KPI 5: Radar Demissional */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-2xs hover:border-gray-300 transition-all flex flex-col justify-between">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
              Radar Demissional
            </span>
            <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 border border-indigo-100 flex items-center justify-center">
              <Undo2 className="w-4 h-4" />
            </div>
          </div>
          <div className="mt-2.5 flex items-baseline gap-1.5">
            <span className="text-2xl font-bold text-indigo-600">
              {loadingKpis ? '...' : (kpis?.alertas_demissionais_ativos ?? kpis?.radar_demissional_pendentes ?? 0)}
            </span>
            <span className="text-[11px] text-gray-500">colaboradores</span>
          </div>
          <div className="mt-2.5">
            <span className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-100 rounded px-2 py-0.5 inline-block font-medium">
              {kpis?.devolucoes_pendentes_confirmadas ?? kpis?.total_devolucoes_pendentes ?? 0} EPIs a recolher
            </span>
          </div>
        </div>
      </div>

      {/* Tabs Navigation & Search Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-gray-200 pb-3">
        <div className="flex items-center gap-1.5 overflow-x-auto">
          <button
            onClick={() => handleTabChange('mapa')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'mapa'
                ? 'bg-primary-600 text-white shadow-xs'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <Users className="w-4 h-4" /> Mapa por Cargo
          </button>

          <button
            onClick={() => handleTabChange('entregas')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'entregas'
                ? 'bg-primary-600 text-white shadow-xs'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <FileText className="w-4 h-4" /> Fichas de Entrega
          </button>

          <button
            onClick={() => handleTabChange('estoque')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'estoque'
                ? 'bg-primary-600 text-white shadow-xs'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <Package className="w-4 h-4" /> Estoque & Kardex
          </button>

          <button
            onClick={() => handleTabChange('vigencias')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'vigencias'
                ? 'bg-primary-600 text-white shadow-xs'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <Calendar className="w-4 h-4" /> Vigência & Trocas
          </button>

          <button
            onClick={() => handleTabChange('devolucoes')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'devolucoes'
                ? 'bg-primary-600 text-white shadow-xs'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <Undo2 className="w-4 h-4" /> Radar Demissional
          </button>

          <button
            onClick={() => handleTabChange('matriz')}
            className={`px-3.5 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 whitespace-nowrap ${
              activeTab === 'matriz'
                ? 'bg-primary-600 text-white shadow-xs'
                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
            }`}
          >
            <TableIcon className="w-4 h-4" /> Matriz de EPIs
          </button>
        </div>

        <div className="relative min-w-[240px]">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-gray-400" />
          <input
            type="text"
            placeholder="Filtrar por nome, cargo ou EPI..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-1.5 bg-white border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-primary-500 shadow-2xs text-gray-800"
          />
        </div>
      </div>

      {/* Tab Content */}
      {loadingData ? (
        <div className="py-20 text-center">
          <div className="w-8 h-8 border-3 border-primary-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-xs font-medium text-gray-500">Carregando informações do módulo...</p>
        </div>
      ) : activeTab === 'mapa' ? (
        /* TAB 1: MAPA POR CARGO E COLABORADORES */
        <div className="space-y-4">
          {filteredCargos.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center text-xs text-gray-400">
              Nenhum cargo encontrado para os filtros selecionados.
            </div>
          ) : (
            filteredCargos.map((cargo) => (
              <div
                key={cargo.cargo_id}
                className="bg-white rounded-2xl border border-gray-200 shadow-2xs overflow-hidden transition-all"
              >
                {/* Header do Cargo */}
                <div
                  onClick={() => toggleCargo(cargo.cargo_id)}
                  className="p-4 sm:p-5 flex flex-wrap items-center justify-between gap-4 cursor-pointer hover:bg-gray-50/70 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <button className="p-1 rounded-lg text-gray-400 hover:text-gray-700">
                      {expandedCargos[cargo.cargo_id] ? (
                        <ChevronDown className="w-5 h-5" />
                      ) : (
                        <ChevronRight className="w-5 h-5" />
                      )}
                    </button>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold text-gray-900">{cargo.cargo_nome}</span>
                        {cargo.setor_nome && (
                          <span className="text-xs text-gray-500 font-medium">({cargo.setor_nome})</span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-[11px] text-gray-500">
                          {cargo.total_colaboradores} colaboradores no cargo
                        </span>
                        <span className="text-gray-300">&bull;</span>
                        <span className="text-[11px] font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded">
                          {cargo.colaboradores_100_equipados} 100% equipados
                        </span>
                        {cargo.colaboradores_pendentes > 0 && (
                          <span className="text-[11px] font-medium text-rose-700 bg-rose-50 px-2 py-0.5 rounded">
                            {cargo.colaboradores_pendentes} pendentes
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Barra de Progresso de Cobertura */}
                  <div className="flex items-center gap-4">
                    <div className="w-36 text-right">
                      <span className="text-xs font-bold text-gray-900 block">
                        {cargo.percentual_cobertura_cargo}%
                      </span>
                      <div className="w-full bg-gray-100 rounded-full h-2 mt-1 overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all ${
                            cargo.percentual_cobertura_cargo === 100
                              ? 'bg-emerald-500'
                              : cargo.percentual_cobertura_cargo >= 60
                              ? 'bg-amber-500'
                              : 'bg-rose-500'
                          }`}
                          style={{ width: `${cargo.percentual_cobertura_cargo}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Itens de EPI devidos pelo Cargo */}
                <div className="px-5 py-2.5 bg-gray-50/80 border-t border-b border-gray-100 flex flex-wrap items-center gap-2">
                  <span className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                    EPIs Devidos (NR-06 / PGR):
                  </span>
                  {cargo.requisitos_epis.length === 0 ? (
                    <span className="text-[11px] text-gray-400 italic">Nenhum EPI obrigatório cadastrado.</span>
                  ) : (
                    cargo.requisitos_epis.map((req) => (
                      <span
                        key={req.id}
                        className="text-xs bg-white text-gray-700 border border-gray-200 rounded-lg px-2.5 py-1 font-medium flex items-center gap-1 shadow-2xs"
                      >
                        <ShieldCheck className="w-3.5 h-3.5 text-primary-600" />
                        {req.epi_descricao || req.epi_nome || 'EPI'} (Qtd: {req.quantidade})
                      </span>
                    ))
                  )}
                </div>

                {/* Accordion: Tabela de Colaboradores do Cargo */}
                {expandedCargos[cargo.cargo_id] && (
                  <div className="p-4 sm:p-5 overflow-x-auto">
                    <table className="w-full text-left text-xs">
                      <thead>
                        <tr className="border-b border-gray-100 text-gray-400 font-semibold uppercase text-[10px]">
                          <th className="pb-2.5">Colaborador</th>
                          <th className="pb-2.5">CPF</th>
                          <th className="pb-2.5">Status de Cobertura</th>
                          <th className="pb-2.5">Entregues / Devidos</th>
                          <th className="pb-2.5">Vencidos</th>
                          <th className="pb-2.5 text-right">Ação</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {cargo.colaboradores.map((colab) => (
                          <tr key={colab.colaborador_id} className="hover:bg-gray-50/60 transition-colors">
                            <td className="py-3">
                              <span className="font-bold text-gray-900 block">{colab.colaborador_nome}</span>
                              {colab.demissional_agendado && (
                                <span className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5 font-medium inline-block mt-0.5">
                                  Exame Demissional Agendado
                                </span>
                              )}
                            </td>
                            <td className="py-3 font-mono text-gray-600">{colab.colaborador_cpf}</td>
                            <td className="py-3">
                              {colab.status_cobertura === '100_equipado' ? (
                                <span className="text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-2 py-0.5 inline-flex items-center gap-1">
                                  <CheckCircle2 className="w-3 h-3" /> 100% Equipado
                                </span>
                              ) : colab.status_cobertura === 'parcial' ? (
                                <span className="text-[11px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-0.5 inline-flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" /> Parcial ({colab.epis_faltantes} faltantes)
                                </span>
                              ) : (
                                <span className="text-[11px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-2 py-0.5 inline-flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" /> Pendente Total
                                </span>
                              )}
                            </td>
                            <td className="py-3 font-medium text-gray-700">
                              {colab.epis_entregues_vigentes} de {colab.epis_devidos} itens
                            </td>
                            <td className="py-3">
                              {colab.epis_vencidos > 0 ? (
                                <span className="text-rose-600 font-bold">{colab.epis_vencidos} vencido(s)</span>
                              ) : (
                                <span className="text-gray-400">0</span>
                              )}
                            </td>
                            <td className="py-3 text-right">
                              <button
                                onClick={() => handleOpenEntregaForColaborador(colab.colaborador_id)}
                                className="px-3 py-1.5 bg-primary-50 text-primary-700 hover:bg-primary-100 rounded-lg font-bold text-xs transition-colors shadow-2xs inline-flex items-center gap-1"
                              >
                                <Plus className="w-3.5 h-3.5" /> Entregar EPI
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      ) : activeTab === 'entregas' ? (
        /* TAB 2: FICHAS DE ENTREGA (HISTÓRICO E GESTÃO DE ASSINATURAS) */
        <div className="bg-white rounded-2xl border border-gray-200 shadow-2xs p-5 space-y-4">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary-600" /> Fichas de Entrega de EPI Emitidas
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                Histórico completo de fichas com biometria facial, assinatura digital na tela e hash de integridade NR-06.
              </p>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setEntregaStatusFilter('todos')}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  entregaStatusFilter === 'todos' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                Todas
              </button>
              <button
                onClick={() => setEntregaStatusFilter('entregue')}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  entregaStatusFilter === 'entregue' ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                Assinadas
              </button>
              <button
                onClick={() => setEntregaStatusFilter('pendente_assinatura')}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  entregaStatusFilter === 'pendente_assinatura' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                Pendentes
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-gray-100 text-gray-400 font-semibold uppercase text-[10px]">
                  <th className="pb-2.5">Ficha</th>
                  <th className="pb-2.5">Colaborador</th>
                  <th className="pb-2.5">Cargo / Setor</th>
                  <th className="pb-2.5">Data Emissão</th>
                  <th className="pb-2.5 text-center">Itens</th>
                  <th className="pb-2.5">Método</th>
                  <th className="pb-2.5">Status</th>
                  <th className="pb-2.5 text-center">Evidências</th>
                  <th className="pb-2.5 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredEntregas.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-gray-400">
                      Nenhuma ficha de entrega encontrada.
                    </td>
                  </tr>
                ) : (
                  filteredEntregas.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="py-3 font-mono font-bold text-gray-900">#{item.id}</td>
                      <td className="py-3">
                        <span className="font-bold text-gray-900 block">{item.colaborador_nome}</span>
                        <span className="text-[11px] font-mono text-gray-500">{item.colaborador_cpf}</span>
                      </td>
                      <td className="py-3 text-gray-700">
                        {item.cargo_nome}
                        {item.setor_nome && <span className="text-gray-400 block text-[11px]">({item.setor_nome})</span>}
                      </td>
                      <td className="py-3 text-gray-600">
                        {new Date(item.data_entrega).toLocaleDateString('pt-BR')}
                      </td>
                      <td className="py-3 text-center">
                        <span className="font-bold text-gray-800 bg-gray-100 px-2 py-0.5 rounded">
                          {item.itens.length}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className="text-[10px] text-gray-600 bg-gray-50 border border-gray-200 px-1.5 py-0.5 rounded">
                          {item.metodo_assinatura === 'link_celular' ? 'Link Celular' : 'Presencial'}
                        </span>
                      </td>
                      <td className="py-3">
                        {item.status === 'entregue' ? (
                          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5 inline-flex items-center gap-1">
                            <CheckCircle2 className="w-3 h-3" /> Assinada
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5 inline-flex items-center gap-1">
                            <Clock className="w-3 h-3" /> Pendente
                          </span>
                        )}
                      </td>
                      <td className="py-3 text-center">
                        <div className="flex items-center justify-center gap-1.5 text-gray-400">
                          {item.foto_colaborador_url ? (
                            <span title="Selfie capturada"><Camera className="w-3.5 h-3.5 text-emerald-600" /></span>
                          ) : (
                            <span title="Sem selfie"><Camera className="w-3.5 h-3.5 text-gray-300" /></span>
                          )}
                          {item.assinatura_colaborador_url ? (
                            <span title="Assinatura capturada"><PenTool className="w-3.5 h-3.5 text-emerald-600" /></span>
                          ) : (
                            <span title="Sem assinatura"><PenTool className="w-3.5 h-3.5 text-gray-300" /></span>
                          )}
                          {item.hash_integridade && (
                            <span title="Hash SHA-256 gerado"><ShieldCheck className="w-3.5 h-3.5 text-primary-600" /></span>
                          )}
                        </div>
                      </td>
                      <td className="py-3 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          {/* Visualizar Detalhes */}
                          <button
                            onClick={() => handleViewDetalhes(item)}
                            title="Visualizar ficha e auditoria"
                            className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-xs font-medium transition-colors"
                          >
                            Ver Ficha
                          </button>

                          {/* Baixar PDF */}
                          <button
                            onClick={() => handleDownloadPdf(item)}
                            title="Baixar PDF Oficial NR-06"
                            className="p-1.5 bg-primary-50 hover:bg-primary-100 text-primary-700 rounded-lg transition-colors shadow-2xs"
                          >
                            <FileDown className="w-4 h-4" />
                          </button>

                          {/* Se pendente, ações de assinatura rápida */}
                          {item.status === 'pendente_assinatura' && (
                            <>
                              {item.link_assinatura_publico && (
                                <>
                                  <button
                                    onClick={() => handleCopyLink(item)}
                                    title="Copiar link de autoatendimento"
                                    className="p-1.5 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg transition-colors shadow-2xs"
                                  >
                                    {copiedTokenId === item.id ? (
                                      <Check className="w-4 h-4 text-emerald-600" />
                                    ) : (
                                      <Copy className="w-4 h-4" />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => handleShareWhatsApp(item)}
                                    title="Enviar por WhatsApp"
                                    className="p-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg transition-colors shadow-2xs"
                                  >
                                    <MessageCircle className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                              <button
                                onClick={() => handleOpenAssinatura(item)}
                                title="Assinar presencialmente agora"
                                className="px-2.5 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-bold transition-colors shadow-2xs flex items-center gap-1"
                              >
                                <PenTool className="w-3.5 h-3.5" /> Assinar
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : activeTab === 'estoque' ? (
        /* TAB 3: ESTOQUE E KARDEX */
        <div className="space-y-6">
          {/* Card de Saldos Atuais */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xs p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Package className="w-4 h-4 text-primary-600" /> Saldos Atuais em Estoque (Físico vs Em Uso)
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-400 font-semibold uppercase text-[10px]">
                    <th className="pb-2.5">Equipamento (EPI)</th>
                    <th className="pb-2.5">CA Ativo</th>
                    <th className="pb-2.5 text-center">Disponível</th>
                    <th className="pb-2.5 text-center">Em Uso</th>
                    <th className="pb-2.5 text-center">Avariado</th>
                    <th className="pb-2.5 text-center">Descartado</th>
                    <th className="pb-2.5 text-center">Mínimo</th>
                    <th className="pb-2.5 text-right">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {estoqueSaldos.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-gray-400">
                        Nenhum item em estoque registrado.
                      </td>
                    </tr>
                  ) : (
                    estoqueSaldos.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50/60 transition-colors">
                        <td className="py-3 font-bold text-gray-900">{item.epi_descricao}</td>
                        <td className="py-3 font-mono text-gray-600">{item.ca_numero || 'Regulamentado'}</td>
                        <td className="py-3 text-center font-bold text-emerald-600">{item.saldo_disponivel}</td>
                        <td className="py-3 text-center font-medium text-primary-700">{item.saldo_em_uso}</td>
                        <td className="py-3 text-center text-amber-600">{item.saldo_avariado}</td>
                        <td className="py-3 text-center text-gray-400">{item.saldo_descartado}</td>
                        <td className="py-3 text-center font-medium text-gray-500">{item.estoque_minimo}</td>
                        <td className="py-3 text-right">
                          {item.alerta_ruptura ? (
                            <span className="text-[10px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-0.5">
                              Ruptura Crítica
                            </span>
                          ) : (
                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                              Estável
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Card de Kardex (Extrato de Movimentações) */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xs p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <Layers className="w-4 h-4 text-primary-600" /> Extrato Kardex Transacional
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-400 font-semibold uppercase text-[10px]">
                    <th className="pb-2.5">Data / Hora</th>
                    <th className="pb-2.5">Tipo</th>
                    <th className="pb-2.5">EPI</th>
                    <th className="pb-2.5 text-center">Quantidade</th>
                    <th className="pb-2.5 text-center">Saldo Anterior</th>
                    <th className="pb-2.5 text-center">Saldo Posterior</th>
                    <th className="pb-2.5">Motivo / NF</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {movimentacoes.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="py-8 text-center text-gray-400">
                        Nenhuma movimentação registrada no Kardex.
                      </td>
                    </tr>
                  ) : (
                    movimentacoes.map((mov) => (
                      <tr key={mov.id} className="hover:bg-gray-50/60 transition-colors">
                        <td className="py-2.5 text-gray-500">
                          {new Date(mov.created_at).toLocaleDateString('pt-BR')}{' '}
                          {new Date(mov.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                        </td>
                        <td className="py-2.5">
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                              mov.quantidade > 0
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-rose-50 text-rose-700 border border-rose-200'
                            }`}
                          >
                            {mov.tipo}
                          </span>
                        </td>
                        <td className="py-2.5 font-medium text-gray-900">{mov.epi_descricao}</td>
                        <td className="py-2.5 text-center font-bold">
                          {mov.quantidade > 0 ? `+${mov.quantidade}` : mov.quantidade}
                        </td>
                        <td className="py-2.5 text-center text-gray-500">{mov.saldo_anterior}</td>
                        <td className="py-2.5 text-center font-bold text-gray-800">{mov.saldo_posterior}</td>
                        <td className="py-2.5 text-gray-600">
                          {mov.motivo} {mov.nota_fiscal ? `(NF: ${mov.nota_fiscal})` : ''}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : activeTab === 'vigencias' ? (
        /* TAB 4: CRONOGRAMA DE VIGÊNCIAS E INSPEÇÕES */
        <div className="bg-white rounded-2xl border border-gray-200 shadow-2xs p-5 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
              <Calendar className="w-4 h-4 text-primary-600" /> Calendário de Troca Periódica & Inspeção de EPIs
            </h3>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setVigenciaFilter('todos')}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  vigenciaFilter === 'todos' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                Todos
              </button>
              <button
                onClick={() => setVigenciaFilter('criticos')}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  vigenciaFilter === 'criticos' ? 'bg-amber-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                A Vencer (30d)
              </button>
              <button
                onClick={() => setVigenciaFilter('vencidos')}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  vigenciaFilter === 'vencidos' ? 'bg-rose-600 text-white' : 'bg-gray-100 text-gray-700'
                }`}
              >
                Vencidos
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-gray-100 text-gray-400 font-semibold uppercase text-[10px]">
                  <th className="pb-2.5">Colaborador / Cargo</th>
                  <th className="pb-2.5">Equipamento (EPI)</th>
                  <th className="pb-2.5">CA</th>
                  <th className="pb-2.5">Entregue em</th>
                  <th className="pb-2.5">Previsão de Troca</th>
                  <th className="pb-2.5 text-center">Dias Restantes</th>
                  <th className="pb-2.5 text-right">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredVigencias.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-gray-400">
                      Nenhum item localizado para este filtro.
                    </td>
                  </tr>
                ) : (
                  filteredVigencias.map((item) => (
                    <tr key={item.entrega_item_id} className="hover:bg-gray-50/60 transition-colors">
                      <td className="py-3">
                        <span className="font-bold text-gray-900 block">{item.colaborador_nome}</span>
                        <span className="text-[11px] text-gray-500">{item.cargo_nome}</span>
                      </td>
                      <td className="py-3 font-medium text-gray-800">{item.descricao_epi || item.epi_descricao}</td>
                      <td className="py-3 font-mono text-gray-600">{item.ca_numero || 'N/A'}</td>
                      <td className="py-3 text-gray-600">
                        {item.data_entrega ? new Date(item.data_entrega).toLocaleDateString('pt-BR') : '-'}
                      </td>
                      <td className="py-3 font-medium text-gray-900">
                        {item.data_prevista_troca
                          ? new Date(item.data_prevista_troca).toLocaleDateString('pt-BR')
                          : '-'}
                      </td>
                      <td className="py-3 text-center font-bold">
                        {(item.dias_restantes ?? item.dias_para_troca ?? 0) < 0 ? (
                          <span className="text-rose-600">Vencido ({Math.abs(item.dias_restantes ?? item.dias_para_troca ?? 0)}d atrás)</span>
                        ) : (
                          <span>{item.dias_restantes ?? item.dias_para_troca ?? 0} dias</span>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        {item.status_vigencia === 'vencido' ? (
                          <span className="text-[10px] font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded px-2 py-0.5">
                            Vencido
                          </span>
                        ) : (item.status_vigencia as string) === 'a_vencer' || item.status_vigencia === 'alerta_30_dias' || item.status_vigencia === 'alerta_15_dias' ? (
                          <span className="text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-0.5">
                            A Vencer
                          </span>
                        ) : (
                          <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-2 py-0.5">
                            Vigente
                          </span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      ) : activeTab === 'devolucoes' ? (
        /* TAB 5: DEVOLUÇÕES E RADAR DEMISSIONAL */
        <div className="space-y-6">
          {/* Radar Demissional Card */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xs p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
                  <Undo2 className="w-4 h-4 text-indigo-600" /> Radar Demissional (Alerta Prévio & Devoluções Pendentes)
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  Colaboradores com exame demissional agendado ou desligamento formal que possuem EPIs a devolver.
                </p>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-400 font-semibold uppercase text-[10px]">
                    <th className="pb-2.5">Colaborador</th>
                    <th className="pb-2.5">Cargo / Setor</th>
                    <th className="pb-2.5">Exame Demissional</th>
                    <th className="pb-2.5 text-center">EPIs com o Colaborador</th>
                    <th className="pb-2.5 text-right">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {radarDemissional.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="py-8 text-center text-gray-400">
                        Nenhum colaborador com desligamento ou demissional pendente de devolução.
                      </td>
                    </tr>
                  ) : (
                    radarDemissional.map((item) => (
                      <tr key={item.colaborador_id} className="hover:bg-gray-50/60 transition-colors">
                        <td className="py-3">
                          <span className="font-bold text-gray-900 block">{item.colaborador_nome}</span>
                          <span className="text-[11px] font-mono text-gray-500">{item.colaborador_cpf}</span>
                        </td>
                        <td className="py-3 text-gray-700">
                          {item.cargo_nome} ({item.setor_nome})
                        </td>
                        <td className="py-3">
                          {(item.data_exame_demissional || item.data_agendamento) ? (
                            <div>
                              <span className="font-bold text-indigo-700 block">
                                {new Date(item.data_exame_demissional || item.data_agendamento).toLocaleDateString('pt-BR')}
                              </span>
                              <span className="text-[10px] text-gray-500">Status: {item.status_exame}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400">Desligamento Direto</span>
                          )}
                        </td>
                        <td className="py-3 text-center">
                          <span className="font-bold text-gray-900 bg-gray-100 px-2.5 py-1 rounded-lg">
                            {item.total_epis_a_devolver ?? item.total_epis_em_posse ?? 0} equipamentos
                          </span>
                        </td>
                        <td className="py-3 text-right">
                          <button
                            onClick={() =>
                              handleOpenDevolucaoForColaborador(item.colaborador_id, item.agendamento_id || undefined)
                            }
                            className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-xs font-bold transition-colors shadow-2xs inline-flex items-center gap-1"
                          >
                            <Undo2 className="w-3.5 h-3.5" /> Registrar Devolução
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Histórico de Devoluções Realizadas */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xs p-5">
            <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 text-emerald-600" /> Histórico de Devoluções & Inspeção Técnica
            </h3>

            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-400 font-semibold uppercase text-[10px]">
                    <th className="pb-2.5">Data Devolução</th>
                    <th className="pb-2.5">Colaborador</th>
                    <th className="pb-2.5">Equipamento</th>
                    <th className="pb-2.5">Estado de Conservação</th>
                    <th className="pb-2.5">Destino no Estoque</th>
                    <th className="pb-2.5">Motivo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {devolucoes.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-8 text-center text-gray-400">
                        Nenhuma devolução registrada até o momento.
                      </td>
                    </tr>
                  ) : (
                    devolucoes.map((dev) => (
                      <tr key={dev.id} className="hover:bg-gray-50/60 transition-colors">
                        <td className="py-2.5 text-gray-500">
                          {new Date(dev.data_devolucao).toLocaleDateString('pt-BR')}
                        </td>
                        <td className="py-2.5 font-bold text-gray-900">{dev.colaborador_nome}</td>
                        <td className="py-2.5 font-medium text-gray-800">{dev.descricao_epi || dev.epi_descricao}</td>
                        <td className="py-2.5">
                          <span
                            className={`text-[10px] font-bold px-2 py-0.5 rounded ${
                              dev.estado_conservacao === 'reaproveitavel'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : dev.estado_conservacao === 'avariado_descarte'
                                ? 'bg-amber-50 text-amber-700 border border-amber-200'
                                : 'bg-rose-50 text-rose-700 border border-rose-200'
                            }`}
                          >
                            {dev.estado_conservacao === 'reaproveitavel'
                              ? 'Higienizado / Íntegro'
                              : dev.estado_conservacao === 'avariado_descarte'
                              ? 'Danificado / Baixa'
                              : 'Não Devolvido'}
                          </span>
                        </td>
                        <td className="py-2.5 text-gray-700">
                          {dev.destino_estoque === 'retornou_estoque'
                            ? 'Voltou ao Estoque'
                            : dev.destino_estoque === 'descarte_baixa'
                            ? 'Enviado para Descarte'
                            : 'Sem Movimentação'}
                        </td>
                        <td className="py-2.5 text-gray-600">{dev.motivo}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        /* TAB 6: MATRIZ DE EPIS (PGR / VERSÃO VIGENTE) */
        <div className="space-y-6">
          {/* Card: Versão Vigente da Matriz */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-2xs p-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-4 border-b border-gray-100">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary-50 text-primary-600 flex items-center justify-center border border-primary-100">
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-bold text-gray-900">
                      Matriz de EPIs da Empresa &bull; Versão #{matriz?.versao_atual?.numero_versao || 1}
                    </h3>
                    <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
                      Vigente
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {matriz?.versao_atual?.motivo_revisao || 'Matriz de referência para emissão e auditoria de EPIs'}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3 text-xs">
                <div className="text-right">
                  <span className="text-[10px] text-gray-400 block font-semibold uppercase">Início de Vigência</span>
                  <span className="font-bold text-gray-800">
                    {matriz?.versao_atual?.data_inicio_vigencia
                      ? new Date(matriz.versao_atual.data_inicio_vigencia).toLocaleDateString('pt-BR')
                      : 'Em Vigor'}
                  </span>
                </div>
              </div>
            </div>

            {/* Tabela de Itens da Matriz Vigente */}
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead>
                  <tr className="border-b border-gray-100 text-gray-400 font-semibold uppercase text-[10px]">
                    <th className="pb-2.5">Cargo Vinculado</th>
                    <th className="pb-2.5">Setor</th>
                    <th className="pb-2.5">Equipamento (EPI)</th>
                    <th className="pb-2.5">CA de Referência</th>
                    <th className="pb-2.5 text-center">Quantidade</th>
                    <th className="pb-2.5 text-center">Periodicidade Troca</th>
                    <th className="pb-2.5 text-center">Obrigatoriedade</th>
                    <th className="pb-2.5 text-right">Origem</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {(!matriz?.versao_atual?.itens || matriz.versao_atual.itens.length === 0) ? (
                    <tr>
                      <td colSpan={8} className="py-8 text-center text-gray-400">
                        Nenhuma regra cadastrada na matriz vigente.
                      </td>
                    </tr>
                  ) : (
                    matriz.versao_atual.itens.map((item) => (
                      <tr key={item.id} className="hover:bg-gray-50/60 transition-colors">
                        <td className="py-3 font-bold text-gray-900">{item.cargo_nome}</td>
                        <td className="py-3 text-gray-600">{item.setor_nome || 'Todos os setores'}</td>
                        <td className="py-3 font-medium text-gray-800">{item.epi_descricao || item.epi_nome || 'EPI'}</td>
                        <td className="py-3 font-mono text-gray-600">{item.ca_numero || 'Certificado'}</td>
                        <td className="py-3 text-center font-bold text-gray-900">{item.quantidade}</td>
                        <td className="py-3 text-center font-medium text-gray-700">
                          {item.periodicidade_troca_dias
                            ? `${item.periodicidade_troca_dias} dias (${Math.round(item.periodicidade_troca_dias / 30)} meses)`
                            : 'Conforme Desgaste'}
                        </td>
                        <td className="py-3 text-center">
                          {item.obrigatorio ? (
                            <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded">
                              Obrigatório
                            </span>
                          ) : (
                            <span className="text-[10px] font-medium text-gray-600 bg-gray-100 px-2 py-0.5 rounded">
                              Recomendado
                            </span>
                          )}
                        </td>
                        <td className="py-3 text-right">
                          <span className="text-[10px] font-medium text-primary-700 bg-primary-50 border border-primary-100 px-2 py-0.5 rounded">
                            {(item.regra_origem_tipo || item.origem) === 'REGULATORIO'
                              ? 'PGR / PCMSO'
                              : (item.regra_origem_tipo || item.origem || 'MANUAL')}
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Histórico de Versões da Matriz */}
          {((matriz?.versoes_historicas || matriz?.historico_versoes)?.length ?? 0) > 1 && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-2xs p-5">
              <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4 text-gray-500" /> Acervo Histórico de Revisões da Matriz
              </h3>

              <div className="space-y-2">
                {(matriz?.versoes_historicas || matriz?.historico_versoes || []).map((v) => (
                  <div
                    key={v.id}
                    className="p-3 bg-gray-50 rounded-xl border border-gray-200 flex items-center justify-between text-xs"
                  >
                    <div>
                      <span className="font-bold text-gray-800">Versão #{v.numero_versao}</span>
                      <span className="text-gray-400 mx-2">&bull;</span>
                      <span className="text-gray-600">{v.motivo_revisao || 'Sem motivo registrado'}</span>
                    </div>
                    <div className="text-gray-500">
                      Vigência: {new Date(v.data_inicio_vigencia).toLocaleDateString('pt-BR')} até{' '}
                      {v.data_fim_vigencia ? new Date(v.data_fim_vigencia).toLocaleDateString('pt-BR') : 'Vigente'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* MODAIS */}
      <EntregaEpiModal
        isOpen={isEntregaModalOpen}
        onClose={() => setIsEntregaModalOpen(false)}
        onSuccess={() => {
          toast.success('Ficha de entrega emitida com sucesso!');
          loadKpis();
          loadTabData();
        }}
        empresaId={empresa?.id || 0}
        initialColaboradorId={modalColaboradorId}
      />

      <EntradaEstoqueModal
        isOpen={isEntradaModalOpen}
        onClose={() => setIsEntradaModalOpen(false)}
        onSuccess={() => {
          toast.success('Entrada de compra registrada com sucesso!');
          loadKpis();
          loadTabData();
        }}
        empresaId={empresa?.id || 0}
      />

      <BaixaEstoqueModal
        isOpen={isBaixaModalOpen}
        onClose={() => setIsBaixaModalOpen(false)}
        onSuccess={() => {
          toast.success('Baixa de estoque efetuada com sucesso!');
          loadKpis();
          loadTabData();
        }}
        empresaId={empresa?.id || 0}
      />

      <DevolucaoEpiModal
        isOpen={isDevolucaoModalOpen}
        onClose={() => setIsDevolucaoModalOpen(false)}
        onSuccess={() => {
          toast.success('Devolução registrada com sucesso!');
          loadKpis();
          loadTabData();
        }}
        empresaId={empresa?.id || 0}
        initialColaboradorId={modalColaboradorId}
        initialAgendamentoId={modalAgendamentoId}
      />

      <DetalhesEntregaModal
        isOpen={isDetalhesModalOpen}
        onClose={() => {
          setIsDetalhesModalOpen(false);
          setSelectedEntrega(null);
        }}
        entrega={selectedEntrega}
        onOpenAssinatura={(ent) => {
          setSelectedEntrega(ent);
          setIsAssinarModalOpen(true);
        }}
      />

      <AssinarEntregaModal
        isOpen={isAssinarModalOpen}
        onClose={() => {
          setIsAssinarModalOpen(false);
          setSelectedEntrega(null);
        }}
        onSuccess={() => {
          toast.success('Assinatura presencial concluída com sucesso!');
          loadKpis();
          loadTabData();
        }}
        entrega={selectedEntrega}
      />
    </div>
  );
}
